import { useState, useRef, useEffect } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Beef,
  Map,
  DollarSign,
  Flame,
  Menu,
  X,
  Settings,
  LogOut,
  Bot,
  Calendar,
  Leaf,
  Users,
  Building2,
  ChevronDown,
  Plus,
  ClipboardCheck,
  ClipboardList,
  FileText,
  TrendingUp,
  Crosshair,
  Droplets,
  // Biochar pilot land monitoring sub-pages (added 2026-04-30)
  Target,
  MapPin,
  Activity,
  Wrench,
  BookOpen,
  Brain,
  // Drug/vaccine inventory (mig 110)
  Pill,
  // Fleet Assurance (mig 109): sysadmin-only trust-score worklist
  ShieldCheck,
  // Lantana coverage report (S3, mig 099)
  Sprout,
  // In-app owner assistant (Q&A chat + digest)
  Sparkles,
  // Digital twin (mig 130): three trajectories, one farm
  GitCompareArrows,
  // Pasture pins (mig 124): the herdsman capture surface
  Footprints,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { getPendingEventsCount, getApprovalsInboxCount, getRanch } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { Tab, canSeeTab, ROLE_LABELS } from '../lib/permissions'
import clsx from 'clsx'
import NotificationBell from './NotificationBell'
import SyncStatus from './SyncStatus'
import RouteErrorBoundary from './RouteErrorBoundary'

// All navigation items with role-based visibility
// roles: which roles can see this item (empty = everyone)
// Per-item icon theming: [inactiveBg, inactiveText, activeBg, activeText]
const iconThemes = {
  Dashboard:        ['bg-primary-50',  'text-primary-500',  'bg-primary-100', 'text-primary-700'],
  Cattle:           ['bg-amber-50',    'text-amber-500',    'bg-amber-100',   'text-amber-700'],
  Paddocks:         ['bg-green-50',    'text-green-500',    'bg-green-100',   'text-green-700'],
  Lantana:          ['bg-red-50',      'text-red-400',      'bg-red-100',     'text-red-600'],
  'Grazing Calendar': ['bg-blue-50',   'text-blue-500',     'bg-blue-100',    'text-blue-700'],
  Finance:          ['bg-yellow-50',   'text-yellow-600',   'bg-yellow-100',  'text-yellow-700'],
  Inventory:        ['bg-rose-50',      'text-rose-500',     'bg-rose-100',    'text-rose-700'],
  Biochar:          ['bg-orange-50',   'text-orange-500',   'bg-orange-100',  'text-orange-700'],
  Reports:          ['bg-purple-50',   'text-purple-500',   'bg-purple-100',  'text-purple-700'],
  Agents:           ['bg-indigo-50',   'text-indigo-500',   'bg-indigo-100',  'text-indigo-700'],
  Assistant:        ['bg-fuchsia-50',  'text-fuchsia-500',  'bg-fuchsia-100', 'text-fuchsia-700'],
  Users:            ['bg-teal-50',     'text-teal-500',     'bg-teal-100',    'text-teal-700'],
  'Market Prices':  ['bg-emerald-50',  'text-emerald-500',  'bg-emerald-100', 'text-emerald-700'],
  'Land Monitoring':['bg-lime-50',     'text-lime-500',     'bg-lime-100',    'text-lime-700'],
  Dairy:            ['bg-sky-50',      'text-sky-500',      'bg-sky-100',     'text-sky-700'],
  'My Lantana':     ['bg-red-50',      'text-red-400',      'bg-red-100',     'text-red-600'],
  'My Biochar':     ['bg-orange-50',   'text-orange-500',   'bg-orange-100',  'text-orange-700'],
  // Biochar pilot land monitoring sub-pages reuse the lime palette so they
  // visually cluster with the parent "Land Monitoring" entry.
  'Pilot Tracker':  ['bg-lime-50',     'text-lime-500',     'bg-lime-100',    'text-lime-700'],
  'Field Markers':  ['bg-lime-50',     'text-lime-500',     'bg-lime-100',    'text-lime-700'],
  'Cycle Progress': ['bg-lime-50',     'text-lime-500',     'bg-lime-100',    'text-lime-700'],
  Instruments:      ['bg-lime-50',     'text-lime-500',     'bg-lime-100',    'text-lime-700'],
  'Field Log':      ['bg-lime-50',     'text-lime-500',     'bg-lime-100',    'text-lime-700'],
  'Lantana Coverage':['bg-lime-50',    'text-lime-500',     'bg-lime-100',    'text-lime-700'],
  // Pasture pins — ground truth from the grass; clusters with the land pages.
  Pasture:          ['bg-lime-50',     'text-lime-500',     'bg-lime-100',    'text-lime-700'],
  // AI Model Registry (S3 of biochar pilot, mig 098). Sysadmin-only nav;
  // violet palette to visually separate it from the lime LM cluster.
  'AI Models':      ['bg-violet-50',   'text-violet-500',   'bg-violet-100',  'text-violet-700'],
  // Fleet Assurance — primary (forest) palette; the integrity/trust surface.
  'Fleet Assurance':['bg-primary-50',  'text-primary-500',  'bg-primary-100', 'text-primary-700'],
}

// Nav items map 1:1 to Tab enums in ../lib/permissions.js.
// Role-based visibility is driven by canSeeTab() — no hardcoded role lists here.
const allNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, tab: Tab.DASHBOARD },
  { name: 'Cattle', href: '/cattle', icon: Beef, tab: Tab.CATTLE },
  { name: 'Kraal Census', href: '/census', icon: ClipboardList, tab: Tab.CENSUS },
  // Pasture pins — herdsman + manager capture surface (pin protocol Phase 2).
  { name: 'Pasture', href: '/pasture', icon: Footprints, tab: Tab.PASTURE },
  { name: 'Dairy', href: '/dairy', icon: Droplets, tab: Tab.DAIRY, dairyOnly: true },
  { name: 'Paddocks', href: '/paddocks', icon: Map, tab: Tab.PADDOCKS },
  { name: 'Lantana', href: '/lantana', icon: Leaf, tab: Tab.LANTANA },
  { name: 'Grazing Calendar', href: '/grazing', icon: Calendar, tab: Tab.GRAZING },
  { name: 'Finance', href: '/finance', icon: DollarSign, tab: Tab.FINANCE },
  { name: 'Inventory', href: '/inventory', icon: Pill, tab: Tab.INVENTORY },
  { name: 'Market Prices', href: '/market-prices', icon: TrendingUp, tab: Tab.MARKET_PRICES },
  { name: 'Land Monitoring', href: '/land', icon: Crosshair, tab: Tab.LAND_MONITORING },
  // Biochar pilot land monitoring sub-pages (S1+S2, migs 092-097). Tab
  // gating: PILOT_PROJECTS visible to owner + system_admin; the rest fall
  // under LAND_MONITORING (land_monitor + system_admin). See
  // .claude/plans/biochar-pilot-land-monitoring.md.
  { name: 'Pilot Tracker',   href: '/land/pilot',          icon: Target,   tab: Tab.PILOT_PROJECTS },
  { name: 'Field Markers',   href: '/land/markers',        icon: MapPin,   tab: Tab.LAND_MONITORING },
  { name: 'Cycle Progress',  href: '/land/cycle-progress', icon: Activity, tab: Tab.LAND_MONITORING },
  { name: 'Instruments',     href: '/land/instruments',    icon: Wrench,   tab: Tab.LAND_MONITORING },
  { name: 'Field Log',       href: '/land/field-log',      icon: BookOpen, tab: Tab.LAND_MONITORING },
  { name: 'Lantana Coverage', href: '/land/lantana-coverage', icon: Sprout, tab: Tab.LAND_MONITORING },
  { name: 'Biochar', href: '/biochar', icon: Flame, tab: Tab.BIOCHAR },
  { name: 'Reports', href: '/reports', icon: FileText, tab: Tab.REPORTS },
  // The digital twin: three trajectories + the assumptions ledger.
  { name: 'The Twin', href: '/twin', icon: GitCompareArrows, tab: Tab.TWIN },
  { name: 'Assistant', href: '/assistant', icon: Sparkles, tab: Tab.ASSISTANT },
  { name: 'Agents', href: '/agents', icon: Bot, tab: Tab.AGENTS },
  { name: 'Users', href: '/users', icon: Users, tab: Tab.USERS },
  // AI Model Registry — sysadmin-only via Tab.AI_MODELS gating.
  { name: 'AI Models', href: '/ai-models', icon: Brain, tab: Tab.AI_MODELS },
  // Fleet Assurance — sysadmin-only via Tab.FLEET_ASSURANCE gating.
  { name: 'Fleet Assurance', href: '/fleet-assurance', icon: ShieldCheck, tab: Tab.FLEET_ASSURANCE },
  // Twin Fleet — cross-farm twin worklist (plan Phase 6), gated like Fleet Assurance.
  { name: 'Twin Fleet', href: '/twin/fleet', icon: GitCompareArrows, tab: Tab.TWIN_FLEET },
  // Assignment-scoped landing pages for narrow roles (Session 2). Both routes
  // lean on backend auto-scoping: a bush_clearer hitting /lantana-schedule
  // sees only rows with assigned_to = self; a biochar_operator hitting
  // /biochar/batches sees only rows with operator_id = self.
  { name: 'My Lantana', href: '/my-lantana', icon: Leaf, tab: Tab.MY_LANTANA_ASSIGNMENTS },
  { name: 'My Biochar', href: '/my-biochar', icon: Flame, tab: Tab.MY_BIOCHAR_BATCHES },
]

function getNavigationForRole(role, enterpriseType = 'beef') {
  return allNavigation.filter(item => {
    if (!canSeeTab(role, item.tab)) return false
    if (item.dairyOnly && enterpriseType === 'beef') return false
    return true
  })
}

function Logo({ className = '', compact = false }) {
  if (compact) {
    return (
      <img src="/logo-mark.png" alt="WildeBeef" className={clsx('h-10 w-auto', className)} />
    )
  }

  return (
    <img src="/logo.png" alt="WildeBeef" className={clsx('h-12 w-auto', className)} />
  )
}

// Settings Dropdown Component
function SettingsDropdown({ currentRanchId }) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const navigate = useNavigate()
  const { role, logout } = useAuth()

  // Fetch pending events count for badge
  const { data: pendingData } = useQuery({
    queryKey: ['pendingEventsCount', currentRanchId],
    queryFn: () => getPendingEventsCount(currentRanchId),
    refetchInterval: 30000, // Refresh every 30 seconds
  })
  const pendingCount = pendingData?.pending_count || 0

  // Approvals Inbox count — scoped to this ranch so managers don't see
  // every other ranch's queue in the badge. System admins still get a
  // cross-ranch view when they hit the page itself.
  const canSeeApprovals = ['manager', 'owner', 'system_admin', 'admin'].includes(role)
  const { data: approvalsCountData } = useQuery({
    queryKey: ['approvalsInboxCount', currentRanchId],
    queryFn: () => getApprovalsInboxCount(currentRanchId),
    refetchInterval: 30000,
    enabled: canSeeApprovals,
  })
  const approvalsCount = approvalsCountData?.count || 0

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Dropdown entries — each lists its own visibility predicate so it can
  // pull from canSeeTab() (nav-driven) or explicit role gates (admin-only ops).
  const allMenuItems = [
    {
      name: 'Approvals Inbox',
      href: '/approvals',
      icon: ClipboardCheck,
      description: 'Review pending events, expenses & income',
      badge: approvalsCount,
      visible: canSeeApprovals,
    },
    {
      name: 'Pending Approvals',
      href: '/cattle/events/pending',
      icon: ClipboardCheck,
      description: 'Cattle events awaiting review (with bulk approve)',
      badge: pendingCount,
      // S6: cattle-event approval is the manager's job (APPROVE_CATTLE_EVENTS);
      // owners approve finance via the Approvals Inbox above.
      visible: canSeeTab(role, Tab.CATTLE) && ['manager', 'system_admin', 'admin'].includes(role),
    },
    {
      name: 'Ranch Settings',
      href: '/ranch/settings',
      icon: Building2,
      description: 'View & edit ranch boundaries',
      // S6: editing ranch boundaries is a write — owners are read-only.
      visible: ['manager', 'system_admin', 'admin'].includes(role),
    },
    {
      name: 'Ranch Setup',
      href: '/ranch/setup',
      icon: Plus,
      description: 'Add a new ranch',
      // Spec rule 13: only system owners create ranches.
      visible: ['system_admin', 'admin'].includes(role),
    },
    {
      name: 'User Management',
      href: '/users',
      icon: Users,
      description: 'Manage team members',
      // Spec rule 5: manager handles all Users tab work.
      visible: canSeeTab(role, Tab.USERS),
    },
  ]

  const menuItems = allMenuItems.filter(item => item.visible)

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Settings"
        data-testid="settings-menu"
        className={clsx(
          "flex items-center gap-1 p-2 rounded-md transition-colors relative",
          isOpen
            ? "bg-earth-100 text-earth-900"
            : "text-earth-500 hover:text-earth-700 hover:bg-earth-100"
        )}
      >
        <Settings className="h-5 w-5" />
        {/* The Approvals Inbox is the unified, deduplicated queue of everything
            the caller can act on (cattle events for managers, finance for
            owners, both for system admins). The "Pending Approvals" cattle page
            is a sub-view of the SAME cattle requests, so summing the two
            double-counted the pile. Drive the alert dot off the inbox alone. */}
        {approvalsCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {approvalsCount > 9 ? '9+' : approvalsCount}
          </span>
        )}
        <ChevronDown className={clsx(
          "h-4 w-4 transition-transform",
          isOpen && "rotate-180"
        )} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-earth-200 py-2 z-50">
          <div className="px-3 py-2 border-b border-earth-100">
            <p className="text-xs font-medium text-earth-500 uppercase tracking-wider">Settings</p>
          </div>
          
          {menuItems.map((item) => (
            <button
              key={item.name}
              onClick={() => {
                navigate(item.href)
                setIsOpen(false)
              }}
              className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-earth-50 transition-colors text-left"
            >
              <item.icon className="h-5 w-5 text-earth-500 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-earth-900">{item.name}</p>
                  {item.badge > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                      {item.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-earth-500">{item.description}</p>
              </div>
            </button>
          ))}

          <div className="border-t border-earth-100 mt-2 pt-2">
            <button
              onClick={() => { setIsOpen(false); logout() }}
              data-testid="sign-out"
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-red-50 transition-colors text-left text-red-600"
            >
              <LogOut className="h-5 w-5" />
              <span className="text-sm font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function Layout({ currentRanchId }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const { user: currentUser, role, userId } = useAuth()

  // Fetch current ranch details for display
  const { data: currentRanch } = useQuery({
    queryKey: ['ranch', currentRanchId],
    queryFn: () => getRanch(currentRanchId),
    enabled: !!currentRanchId,
  })

  // Filter navigation by role and enterprise type
  const enterpriseType = currentRanch?.enterprise_type || 'beef'
  const navigation = getNavigationForRole(role, enterpriseType)

  return (
    <div className="min-h-screen bg-earth-50">
      {/* Mobile sidebar */}
      <div className={clsx(
        'fixed inset-0 z-50 lg:hidden',
        sidebarOpen ? 'block' : 'hidden'
      )}>
        <div className="fixed inset-0 bg-gray-900/80" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl">
          <div className="flex h-20 items-center justify-between px-4 border-b border-earth-200">
            <Logo />
            <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-md hover:bg-earth-100">
              <X className="h-5 w-5 text-earth-600" />
            </button>
          </div>
          <nav className="p-4 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname.startsWith(item.href)
              const theme = iconThemes[item.name] || iconThemes.Dashboard
              const [inBg, inText, actBg, actText] = theme
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={clsx(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-500'
                      : 'text-earth-700 hover:bg-earth-100'
                  )}
                >
                  <span className={clsx('p-1.5 rounded-lg transition-colors', isActive ? actBg : inBg)}>
                    <item.icon className={clsx('h-4 w-4', isActive ? actText : inText)} />
                  </span>
                  {item.name}
                </Link>
              )
            })}
            
            {/* Mobile: Ranch Setup link — system owners only (spec rule 13) */}
            {['system_admin', 'admin'].includes(role) && (
              <div className="border-t border-earth-200 pt-4 mt-4">
                <Link
                  to="/ranch/setup"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-earth-700 hover:bg-earth-100"
                >
                  <Building2 className="h-5 w-5" />
                  Ranch Setup
                </Link>
              </div>
            )}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-earth-200 bg-white px-6">
          <div className="flex h-20 shrink-0 items-center">
            <Logo />
          </div>
          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname.startsWith(item.href)
                const theme = iconThemes[item.name] || iconThemes.Dashboard
                const [inBg, inText, actBg, actText] = theme
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={clsx(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
                        isActive
                          ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-500 ml-[-4px] pl-[16px]'
                          : 'text-earth-700 hover:bg-earth-100 hover:text-earth-900'
                      )}
                    >
                      <span className={clsx('p-1.5 rounded-lg transition-colors', isActive ? actBg : inBg)}>
                        <item.icon className={clsx('h-4 w-4', isActive ? actText : inText)} />
                      </span>
                      {item.name}
                    </Link>
                  </li>
                )
              })}
            </ul>
            
            {/* Bottom section */}
            <div className="border-t border-earth-200 py-4 mt-auto">
              <div className="flex items-center gap-3 px-3 py-2 text-sm text-earth-600">
                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center border-2 border-primary-200">
                  <span className="text-primary-700 font-bold">
                    {currentUser?.full_name ? currentUser.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'DU'}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{currentUser?.full_name || 'User'}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary-100 text-primary-700">
                      {ROLE_LABELS[role] || role}
                    </span>
                  </div>
                  <div className="text-xs text-earth-500">{currentRanch?.name || 'Loading...'}</div>
                </div>
              </div>
            </div>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-earth-200 bg-white/95 backdrop-blur px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="lg:hidden -m-2.5 p-2.5 text-earth-700"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1 items-center">
              {/* Search could go here */}
            </div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Offline / sync indicator */}
              <SyncStatus />

              {/* Notification Bell */}
              <NotificationBell ranchId={currentRanchId} userId={userId} />

              {/* Settings Dropdown */}
              <SettingsDropdown currentRanchId={currentRanchId} />
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="px-4 sm:px-6 lg:px-8">
            {/* Key on pathname so the boundary resets on navigation — a crash
                on one route won't leave the next route rendering the error. */}
            <RouteErrorBoundary key={location.pathname}>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  )
}
